import { Box, Chip, Avatar } from '@mui/material';
import { Bookmark, Check, Zap, ChevronsUp, ChevronUp, Equal, ChevronDown, ChevronsDown } from 'lucide-react';
import { TicketPriority, TicketStatus, TicketType } from '../types';
import { formatUserDate, parseBackendDate } from 'utils/dates';

// ─── Jira palette ───
export const JIRA = {
  blue: '#0C66E9',
  ink: '#172B4D',
  gray: '#44546F',
  faint: '#626F86',
  column: '#F4F5F7',
  cardBorder: '#DFE1E6',
  dropBg: '#E9F2FF'
};

// ─── Jira issue-type icons (colored squares like Jira Cloud) ───
export function JiraTypeIcon({ type, size = 16 }: { type: TicketType; size?: number }) {
  const wrap = (bg: string, circle: boolean, inner: React.ReactNode) => (
    <Box sx={{ width: size, height: size, minWidth: size, borderRadius: circle ? '50%' : '3px', bgcolor: bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{inner}</Box>
  );
  if (type === 'Story') return wrap('#65BA43', false, <Bookmark size={size * 0.62} color="#fff" fill="#fff" />);
  if (type === 'Task') return wrap('#4BADE8', false, <Check size={size * 0.68} color="#fff" strokeWidth={3.5} />);
  if (type === 'Bug') return wrap('#E5493A', true, <Box sx={{ width: size * 0.32, height: size * 0.32, borderRadius: '50%', bgcolor: '#fff' }} />);
  if (type === 'Epic') return wrap('#904EE2', false, <Zap size={size * 0.62} color="#fff" fill="#fff" />);
  return wrap('#4BADE8', false, <Box sx={{ color: '#fff', fontSize: size * 0.68, fontWeight: 800, lineHeight: 1 }}>⤷</Box>);
}

// ─── Jira priority arrows ───
export function PriorityGlyph({ priority, size = 14 }: { priority: TicketPriority; size?: number }) {
  if (priority === 'Highest') return <ChevronsUp size={size} color="#CD1317" title={`Priority: Highest`} />;
  if (priority === 'High') return <ChevronUp size={size} color="#E56910" title={`Priority: High`} />;
  if (priority === 'Medium') return <Equal size={size} color="#E56910" title={`Priority: Medium`} />;
  if (priority === 'Low') return <ChevronDown size={size} color="#22A06B" title={`Priority: Low`} />;
  return <ChevronsDown size={size} color="#6B778C" title={`Priority: Lowest`} />;
}

// ─── Jira story-point circle ───
export function PointsBadge({ points }: { points: number }) {
  return (
    <Box sx={{ minWidth: 20, height: 20, px: 0.5, borderRadius: '50%', bgcolor: '#DFE1E6', color: '#44546F', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600 }}>
      {points}
    </Box>
  );
}

export const TYPE_COLORS: Record<TicketType, string> = {
  Story: '#0052cc',
  Task: '#2e7d32',
  Bug: '#e53935',
  Epic: '#6a1b9a',
  Subtask: '#f57c00'
};

export const TYPE_ICON: Record<TicketType, string> = {
  Story: '◈',
  Task: '✓',
  Bug: '●',
  Epic: '⬣',
  Subtask: '⤷'
};

export const STATUS_COLORS: Record<TicketStatus, string> = {
  Todo: '#6b7280',
  'In Progress': '#2563eb',
  'In Review': '#d97706',
  Done: '#16a34a'
};

export const PRIORITY_COLORS: Record<TicketPriority, string> = {
  Highest: '#dc2626',
  High: '#ea580c',
  Medium: '#ca8a04',
  Low: '#16a34a',
  Lowest: '#6b7280'
};

// Jira-style shorthand: Highest → P1 … Lowest → P5
export const PRIORITY_SHORT: Record<TicketPriority, string> = {
  Highest: 'P1',
  High: 'P2',
  Medium: 'P3',
  Low: 'P4',
  Lowest: 'P5'
};

export function fmtDueDate(iso?: string): string {
  if (!iso) return '';
  return formatUserDate(iso, { day: 'numeric', month: 'short' });
}

export function isOverdue(iso: string | undefined, done: boolean): boolean {
  if (!iso || done) return false;
  const d = parseBackendDate(iso);
  if (!d) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return d.getTime() < today.getTime();
}

const chipBase = { height: 20, fontSize: 10.5, fontWeight: 700, borderRadius: '6px' } as const;

export function TypeChip({ type }: { type: TicketType }) {
  const c = TYPE_COLORS[type] || '#6b7280';
  return (
    <Chip
      label={`${TYPE_ICON[type] || ''} ${type}`}
      size="small"
      sx={{ ...chipBase, bgcolor: `${c}12`, color: c, border: `1px solid ${c}30` }}
    />
  );
}

export function StatusChip({ status, color }: { status: TicketStatus; color?: string }) {
  const c = color || STATUS_COLORS[status] || '#6b7280';
  return <Chip label={status} size="small" sx={{ ...chipBase, bgcolor: `${c}12`, color: c }} />;
}

export function PriorityChip({ priority }: { priority: TicketPriority }) {
  const c = PRIORITY_COLORS[priority] || '#6b7280';
  const arrow = priority === 'Highest' ? '↑↑' : priority === 'High' ? '↑' : priority === 'Low' ? '↓' : priority === 'Lowest' ? '↓↓' : '=';
  return (
    <Chip label={`${arrow} ${priority}`} size="small" variant="outlined" sx={{ ...chipBase, fontWeight: 600, color: c, borderColor: `${c}50`, bgcolor: '#fff' }} />
  );
}

export function PersonAvatar({ name, size = 24, selected }: { name: string; size?: number; selected?: boolean }) {
  if (!name) return <Avatar sx={{ width: size, height: size, fontSize: size * 0.45, bgcolor: '#e5e7eb', color: '#6b7280' }}>?</Avatar>;
  const clean = name.trim();
  // first letters of first 2 words — but always minimum 2 letters (e.g. "Priya" → "PR")
  let initials = clean.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase();
  if (initials.length < 2) initials = clean.slice(0, 2).toUpperCase();
  const colors = ['#7c3aed', '#2563eb', '#059669', '#ea580c', '#db2777', '#0891b2'];
  const idx = name.length % colors.length;
  // selected avatars get slightly bigger letters with tighter padding to the edge
  return <Avatar sx={{ width: size, height: size, fontSize: size * (selected ? 0.5 : 0.38), bgcolor: colors[idx], fontWeight: 700 }}>{initials}</Avatar>;
}

export function getCurrentUserName(fallbackMembers?: string[]): string {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const base64Url = token.split('.')[1];
      if (base64Url) {
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split('')
            .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
        );
        const payload = JSON.parse(jsonPayload);
        if (payload?.fullName) return payload.fullName;
        if (payload?.name) return payload.name;
        if (payload?.sub) {
          const email = String(payload.sub);
          const namePart = email.split('@')[0];
          if (namePart) {
            return namePart.charAt(0).toUpperCase() + namePart.slice(1);
          }
        }
      }
    }
    const stored = localStorage.getItem('fullName') || localStorage.getItem('user_name');
    if (stored) return stored;
  } catch {}
  if (fallbackMembers && fallbackMembers.length > 0) {
    const candidate = fallbackMembers.find((m) => m && m.toLowerCase() !== 'you');
    if (candidate) return candidate;
  }
  return 'Admin';
}
